import { useState } from 'react';
import {
  facilityEscalation,
  facilityOrganization,
  facilityOnCallShiftAdd,
  facilityOnCallShiftRemove,
  facilityStaff,
  type ApiError,
  type FacilityOnCallRole,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { ErrLine, I, btn, pre, sx, useApi, useGuarded, usePage } from './kit';

const ROLE_LABEL: Record<FacilityOnCallRole, string> = { CHARGE_NURSE: 'Charge nurse', AIDE: 'Aide', ADMIN_ON_DUTY: 'Admin on duty' };
const ROLES: FacilityOnCallRole[] = ['CHARGE_NURSE', 'AIDE', 'ADMIN_ON_DUTY'];
const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const INPUT = 'box-sizing:border-box;height:30px;padding:0 9px;border:1px solid rgba(34,39,46,.2);border-radius:7px;background:#FBF8F1;font-size:16px;font-family:inherit;color:#22272E;';
const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;

const daysLabel = (d: number[]) => (d.length === 7 ? 'Every day' : d.map((n) => DAY[n]).join(' '));
const hoursLabel = (s: string, e: string) => (s === e ? 'all day' : `${s} to ${e}`);

/**
 * Who gets paged at this building, and when (the escalation contacts). An alert pages
 * the charge nurse on call first, then the aide, then the admin on duty; a test page
 * from the setup checklist rings the same person. Reads and writes the building's own
 * on-call schedule (GET /organizations/:id/escalation, POST/DELETE .../on-call/shifts).
 * `orgId` names the building when Aithora (Super Admin, Operator) sets it up from Settings
 * › On-call with one building picked; a Facility Admin's is their own.
 */
export function FacilityOnCall({ orgId: named }: { orgId?: string } = {}) {
  const { client, session } = useSession();
  const { facilityId, changed } = usePage();
  const { canWrite, wr } = useGuarded();
  const orgId = named ?? facilityId ?? session?.user.organizationId ?? null;
  const plan = useApi((c, s) => facilityEscalation(c, { params: { id: orgId! }, signal: s }), [orgId], !!orgId);
  const staff = useApi((c, s) => facilityStaff(c, { params: { id: named ?? 'me' }, signal: s }), [named]);
  // Shifts run on the building's clock, not the browser's: an admin travelling, or Aithora
  // setting it up from another state, must not shift the night nurse by an hour.
  const org = useApi((c, s) => facilityOrganization(c, { params: { id: named ?? 'me' }, signal: s }), [named]);
  const browserTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { return null; } })();
  const tz = org.data?.organization.timezone || browserTz || 'America/Chicago';
  const [form, setForm] = useState<{ role: FacilityOnCallRole; userId: string; start: string; end: string } | null>(null);
  const [remove, setRemove] = useState<{ id: string; label: string } | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  if (!orgId) return null;
  const people = (staff.data?.staff ?? []).filter((p) => p.status === 'active');
  const nameOf = (id: string) => people.find((p) => p.id === id)?.name ?? 'Someone who has left';
  const d = plan.data;
  const ready = !!form && !!form.userId && HHMM.test(form.start) && HHMM.test(form.end);

  const add = async () => {
    if (!form || !ready || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await facilityOnCallShiftAdd(client, { params: { id: orgId }, body: { role: form.role, userId: form.userId, days: [0, 1, 2, 3, 4, 5, 6], start: form.start, end: form.end, timezone: tz } });
      setForm(null);
      plan.reload();
      changed();
    } catch (e) {
      setErr(e as ApiError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="settings-on-call" style={sx('margin-top:18px;border-top:1px solid rgba(34,39,46,.14);padding-top:12px;')}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;margin-bottom:6px;')}>
        <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;')} />
        <span style={sx('font-size:16px;font-weight:700;')}>Who gets paged</span>
        <span style={sx('flex:1;')} />
        <a href="#" {...wr(() => setForm({ role: 'CHARGE_NURSE', userId: '', start: '07:00', end: '19:00' }))} style={sx(btn('primary'))}>
          + Add on-call shift
        </a>
      </div>
      <div style={sx('font-size:16px;color:#54606C;margin-bottom:10px;text-wrap:pretty;')}>
        {I('An alert pages the charge nurse on call first, then the aide, then the admin on duty. With nobody on call, it pages you.')}
      </div>
      {plan.error && <ErrLine error={plan.error} style="margin:0 0 10px;" />}
      {d && (
        <div style={sx('display:flex;gap:10px 22px;flex-wrap:wrap;margin-bottom:10px;')}>
          {ROLES.map((r) => (
            <div key={r} style={sx('min-width:150px;')}>
              <div style={sx('font-size:14px;letter-spacing:.09em;text-transform:uppercase;color:#54606C;font-weight:700;')}>{I(ROLE_LABEL[r] + ' now')}</div>
              <div style={sx('font-size:16px;font-weight:700;margin-top:3px;')} data-testid={`on-call-now-${r}`}>
                {I((d.onNow[r] ?? []).map((p) => p.name ?? p.email ?? 'Unnamed').join(', ') || 'Nobody')}
              </div>
            </div>
          ))}
        </div>
      )}
      {form && (
        <div data-testid="on-call-form" style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:7px;margin-bottom:10px;')}>
          <select aria-label="On-call role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as FacilityOnCallRole })} style={sx(INPUT)}>
            {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
          </select>
          <select aria-label="Who is on call" value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })} style={sx(INPUT + 'min-width:170px;')}>
            <option value="">Choose a person</option>
            {people.map((p) => <option key={p.id} value={p.id}>{(p.name ?? p.email ?? 'Unnamed') + (p.title ? ` (${p.title})` : '')}</option>)}
          </select>
          <input type="text" aria-label="Shift starts" placeholder="07:00" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value.trim() })} style={sx(INPUT + 'width:76px;')} />
          <input type="text" aria-label="Shift ends" placeholder="19:00" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value.trim() })} style={sx(INPUT + 'width:76px;')} />
          <span style={sx('font-size:16px;color:#54606C;')}>{I('every day · ' + tz + ' · same start and end is all day')}</span>
          <a href="#" {...wr(() => void add())} aria-disabled={!ready || busy || undefined} style={sx(btn(ready ? 'primary' : 'off'))}>
            Save shift
          </a>
          <a href="#" onClick={(e) => { pre(e); setForm(null); }} style={sx(btn('ghost'))}>Cancel</a>
        </div>
      )}
      {(d?.shifts ?? []).map((s) => (
        <div key={s.id} style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:8px 0;border-top:1px solid rgba(34,39,46,.07);')}>
          <span style={sx('flex:1 1 220px;min-width:0;font-size:16px;')}>
            <b>{I(ROLE_LABEL[s.role] ?? s.role)}</b>
            {I(' · ' + nameOf(s.user_id) + ' · ' + daysLabel(s.days) + ', ' + hoursLabel(s.start_local, s.end_local))}
          </span>
          <a href="#" {...wr(() => setRemove({ id: s.id, label: `${ROLE_LABEL[s.role] ?? s.role}, ${nameOf(s.user_id)}` }))} style={sx(btn('ghost'))}>
            Remove
          </a>
        </div>
      ))}
      {d && !d.shifts.length && <div style={sx('font-size:16px;color:#54606C;padding:6px 0;')}>No on-call shifts yet.</div>}
      <ErrLine error={err} />
      {remove && canWrite && (
        <ConfirmDialog
          title={`Remove ${remove.label}?`}
          body="They stop being paged for this shift. Anyone else on call still is."
          confirmLabel="Remove"
          danger
          onConfirm={async () => {
            await facilityOnCallShiftRemove(client, { params: { id: orgId, entryId: remove.id } });
            plan.reload();
            changed();
          }}
          onClose={() => setRemove(null)}
        />
      )}
    </div>
  );
}
